import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ConfirmDialog, LAYERS, OverlayLayer, useOverlay, useOverlayLayer } from '@comforthub/design-system'
import OverlayPortal from './OverlayPortal'
import { Loader2 } from 'lucide-react'

interface SideDrawerProps {
  onClose: () => void
  loading?: boolean
  /**
   * Asked before the backdrop or Escape closes the drawer. Return false to keep
   * it open, for example while it holds unsaved input (then ask with
   * DiscardChangesDialog). The close() handed to children always closes.
   */
  confirmClose?: () => boolean
  children: (close: () => void) => ReactNode
}

/**
 * Right slide-in panel shared by orders and appointments. It's a modal dialog
 * named by its first heading: focus moves in and stays in, Escape closes it
 * with the same slide-out as the close button, and focus goes back to what
 * opened it. Opened from inside a Dialog (the offering editor, say), it sits
 * above that dialog.
 */
export default function SideDrawer({ onClose, loading, confirmClose, children }: SideDrawerProps) {
  const [mounted, setMounted] = useState(false)
  const closing = useRef(false)
  const headingId = useId()
  const [labelId, setLabelId] = useState<string>()
  const zIndex = useOverlayLayer(LAYERS.drawer)

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  const handleClose = useCallback(() => {
    if (closing.current) return
    closing.current = true
    setMounted(false)
    setTimeout(onClose, 300)
  }, [onClose])

  // The backdrop and Escape: a drawer with unsaved input can say no.
  const requestClose = () => {
    if (closing.current || (confirmClose && !confirmClose())) return
    handleClose()
  }

  const panelRef = useOverlay<HTMLDivElement>({ open: true, onClose: requestClose })

  // Named by its first heading (the order or booking it shows).
  useLayoutEffect(() => {
    const heading = panelRef.current?.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6, [role="heading"]')
    if (heading && !heading.id) heading.id = headingId
    setLabelId(heading?.id)
  })

  return (
    <OverlayPortal>
      <OverlayLayer zIndex={zIndex}>
        <div className="fixed inset-0 flex justify-end overflow-hidden" style={{ zIndex }}>
          <div
            className={`absolute inset-0 bg-slate-950/40 dark:bg-black/60 backdrop-blur-[2px] transition-opacity duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden
            // Pressing the backdrop mustn't take focus out of the drawer.
            onMouseDown={(e) => e.preventDefault()}
            onClick={requestClose}
            data-testid="drawer-backdrop"
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelId}
            tabIndex={-1}
            className={`relative w-full sm:max-w-md md:max-w-lg bg-white dark:bg-[#0c0c0f] border-l border-slate-200 dark:border-zinc-800/80 shadow-2xl flex flex-col h-full z-10 outline-none transition-transform duration-300 ease-out ${
              mounted ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {loading ? (
              <div className="absolute inset-0 bg-white/60 dark:bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-20">
                <Loader2 size={24} className="animate-spin text-violet-500" />
              </div>
            ) : null}
            {children(handleClose)}
          </div>
        </div>
      </OverlayLayer>
    </OverlayPortal>
  )
}

/**
 * "Discard your changes?" for a drawer that holds unsaved input: open it from
 * confirmClose, and discard with the drawer's close().
 */
export function DiscardChangesDialog({
  open,
  onDiscard,
  onKeep,
}: {
  open: boolean
  onDiscard: () => void
  onKeep: () => void
}) {
  return (
    <ConfirmDialog
      open={open}
      tone="danger"
      title="Discard your changes?"
      body="What you typed here hasn't been saved."
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      onConfirm={onDiscard}
      onCancel={onKeep}
    />
  )
}
